/**
 * The markup, drawn over the sheet (`KLQ-20260820-001` phase 3).
 *
 * **A sibling of `PinLayer`, not a replacement.** Both sit over the canvas, both take their
 * coordinates from the same `SheetTransform` (`overlay.ts`), and neither knows what is drawing
 * the sheet underneath — ADR-0006's shared overlay, used a second time, which is the whole
 * reason the seam exists. Pins are a dozen absolutely-positioned buttons; markup is one `<svg>`
 * whose shapes are re-pointed on the same subscription.
 *
 * **Panning does not re-render React.** Every committed stroke is projected by writing its
 * coordinates straight onto its SVG node inside the transform's subscription, because
 * `animation` fires once per frame and re-running a component tree at 60 Hz to move a path
 * is work nobody asked for. React owns *which* shapes exist and what colour they are; the
 * subscription owns only where their points land. The stroke being drawn right now is the one
 * exception and deliberately so: its *shape* is changing, not just its position, so it is
 * ordinary state.
 *
 * **The layer is transparent to pointer events, always.** Not "unless a tool is armed" —
 * always. A drag arrives through `SheetTransform.subscribeDrag`, which reports it in sheet
 * space and suppresses the viewer's pan for as long as the subscription lives, so the SVG never
 * needs to take a pointer event to hear about a stroke. An overlay that went opaque to catch
 * gestures would be an overlay that can swallow a pan by being mounted, and panning an A0 is
 * the gesture this viewer is built around. `pointer-events: none` in the stylesheet is the
 * whole enforcement, and it has no conditional half to get wrong.
 *
 * **A page is a place, not a filter to be applied twice.** The transform names the page it
 * maps, so that is what rows are matched against — never a `page` prop beside it, which is one
 * more thing to disagree with the sheet actually on screen during the frames between a page
 * change and the canvas reopening on it.
 *
 * **The eraser is a hit test, not a pointer target** (owner, 2026-09-17). With `erase` armed the
 * layer listens to the same drag every other tool does and asks, per step, which of the strokes
 * it was told are `erasable` lie within `ERASE_RADIUS_PX` of the pointer's path — so the layer
 * stays transparent to pointer events with the eraser armed too. Which strokes are erasable
 * (this person's, from this session) and what erasing means are `ViewerPage`'s; this layer only
 * reports ids.
 *
 * **What it does not own.** The tool rail, the colour choice, the layer toggles and the text
 * tool's composer are `ViewerPage`'s (issue #71). This file takes the armed tool and the colour
 * as props and hands back a finished geometry; mounting it at all is what "Markup is visible"
 * means, so a toggle hides the layer without asking the server anything.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useMessages } from '@/lib/i18n';

import type { Markup } from './api';
import { geometryOf, type MarkupGeometry, type MarkupKind, type MarkupTool } from './markup';
import type { ScreenPoint, SheetPoint, SheetTransform } from './overlay';
import styles from './Viewer.module.css';

/**
 * How far the pointer must travel, **in screen pixels**, before a stroke has another point in it.
 *
 * Screen rather than sheet units, and that is the fix. The step used to be `0.002` of the sheet,
 * which is a fine grain at fit and 20px or more once an A0 is zoomed in — so the closer somebody
 * zoomed to draw carefully, the more angular their line came out. A pixel step keeps the same
 * fidelity at every zoom; a stroke drawn zoomed in simply carries more points, because it was
 * drawn with more care over less paper.
 *
 * `draw` keeps every step it is given, and the step is also what keeps one `geometry_json` well
 * under the API's 1 MiB body limit: a 5 000px scribble is under two thousand points. The other
 * kinds keep two points whatever happens, so the filter costs them nothing — and it doubles as
 * the "was this a tap?" test at the end of a gesture, where a press that never moved must not
 * leave a zero-length line behind.
 */
const MIN_STEP_PX = 3;

/**
 * How close, in screen pixels, the eraser must pass to a stroke to take it. A fingertip's worth:
 * the eraser is used on phones, and a 2px line is not something a finger can land on.
 */
const ERASE_RADIUS_PX = 12;

/** The projection map's keys for the two strokes that have no id yet. */
const DRAFT_KEY = 'draft';
const LIVE_KEY = 'live';

/** Rendered off-screen until the projection has somewhere real to put it. */
const HIDDEN = { visibility: 'hidden' } as const;

/** The measure label's own node, keyed beside the line it belongs to. */
function labelKey(key: string): string {
  return `${key}:label`;
}

/** A stroke the pointer has let go of, still on its way to the server (`ViewerPage`). */
export interface SendingStroke {
  readonly key: string;
  readonly geometry: MarkupGeometry;
  readonly color: string;
  readonly versionId: string;
}

/** One shape to draw: what it is, and in what colour. */
interface Figure {
  readonly key: string;
  readonly geometry: MarkupGeometry;
  readonly color: string;
}

export interface MarkupLayerProps {
  /** `null` until the canvas has opened. No transform, no strokes — never a guessed position. */
  readonly transform: SheetTransform | null;
  /** The open version's markup rows, exactly as the server sent them. */
  readonly markups: readonly Markup[];
  /**
   * The armed tool, or `null` for none. While it is `null` the layer listens to nothing and the
   * sheet pans as it always did; arming a tool is what turns a drag into a stroke.
   */
  readonly tool: MarkupTool | null;
  /** The colour a stroke in progress is drawn in. Committed rows carry their own. */
  readonly color: string;
  /**
   * Finished strokes the server has not answered for yet, already narrowed to the open
   * version. Drawn exactly like committed rows, so letting go of the pointer changes nothing
   * on screen; each leaves this list in the tick its row enters `markups`.
   */
  readonly sending: readonly SendingStroke[];
  /**
   * A stroke the caller is holding rather than one being dragged — the `text` tool, whose
   * value comes from a composer this layer does not own. Drawn exactly like a committed one.
   */
  readonly draft: MarkupGeometry | null;
  /** A finished stroke, in sheet coordinates. Writing it is the caller's business. */
  readonly onCommit: (geometry: MarkupGeometry) => void;
  /** Row ids the eraser may take. Everything else it passes over. */
  readonly erasable: ReadonlySet<string>;
  /** Ids the eraser just passed over. Erasing them is the caller's business. */
  readonly onErase: (ids: readonly string[]) => void;
}

export function MarkupLayer({
  transform,
  markups,
  tool,
  color,
  sending,
  draft,
  onCommit,
  erasable,
  onErase,
}: MarkupLayerProps) {
  const t = useMessages('drawings');
  const nodes = useRef(new Map<string, SVGElement>());
  // The gesture's points as they arrive. A ref rather than state because the drag listener is
  // the only writer and it needs to read back what it already has; `live` below is the copy
  // React renders from.
  const gesture = useRef<SheetPoint[] | null>(null);
  const [live, setLive] = useState<readonly SheetPoint[] | null>(null);

  // Held in a ref for the reason `ViewerCanvas` holds its own seams in one: a caller passing an
  // inline lambda would otherwise tear the drag subscription down and rebuild it on every
  // render, and a subscription rebuilt mid-gesture is a stroke that loses its own start.
  const commit = useRef(onCommit);
  useEffect(() => {
    commit.current = onCommit;
  }, [onCommit]);
  const erase = useRef(onErase);
  useEffect(() => {
    erase.current = onErase;
  }, [onErase]);

  const page = transform?.page ?? 0;

  const figures = useMemo<readonly Figure[]>(() => {
    const drawn: Figure[] = [];
    for (const row of markups) {
      const geometry = geometryOf(row);
      // Somebody else's drawing, and not this screen's crash (`markup.ts`). A row whose
      // geometry does not match its kind is skipped in silence — no throw, no empty element.
      if (geometry === null) continue;
      if (geometry.page !== page) continue;
      drawn.push({ key: row.id, geometry, color: row.color });
    }
    for (const stroke of sending) {
      if (stroke.geometry.page !== page) continue;
      drawn.push({ key: stroke.key, geometry: stroke.geometry, color: stroke.color });
    }
    if (draft !== null && draft.page === page) {
      drawn.push({ key: DRAFT_KEY, geometry: draft, color });
    }
    const drawing =
      tool === null || tool === 'erase' || live === null ? null : strokeOf(tool, live, page);
    if (drawing !== null) drawn.push({ key: LIVE_KEY, geometry: drawing, color });
    return drawn;
  }, [markups, sending, page, draft, color, tool, live]);

  // The eraser reads these per step, and a subscription rebuilt whenever a stroke lands or
  // leaves would drop the gesture it is in the middle of — `commit`'s reason, again.
  const targets = useRef<readonly Figure[]>([]);
  useEffect(() => {
    targets.current = figures.filter((figure) => erasable.has(figure.key));
  }, [figures, erasable]);

  useEffect(() => {
    if (transform === null || tool !== 'erase') return undefined;
    // Where the last step ended, so a fast swipe is tested along its whole path rather than at
    // the handful of points the pointer happened to report.
    let previous: SheetPoint | null = null;
    return transform.subscribeDrag((drag) => {
      const from = drag.phase === 'start' || previous === null ? drag.point : previous;
      previous = drag.phase === 'end' ? null : drag.point;
      const a = transform.toScreen(from);
      const b = transform.toScreen(drag.point);
      if (a === null || b === null) return;
      const hits = targets.current
        .filter((figure) => touches(figure, a, b, transform, nodes.current))
        .map((figure) => figure.key);
      if (hits.length > 0) erase.current(hits);
    });
  }, [transform, tool]);

  // Arming a tool is subscribing, and subscribing is what suppresses the pan (`overlay.ts`).
  // `text` is deliberately not in it: a text stroke has words in it and a drag has none, so its
  // placement stays an ordinary press and its value arrives back here as `draft`.
  useEffect(() => {
    if (transform === null || tool === null || tool === 'text' || tool === 'erase') {
      return undefined;
    }
    const unsubscribe = transform.subscribeDrag((drag) => {
      if (drag.phase === 'start') {
        gesture.current = [drag.point];
        setLive([drag.point]);
        return;
      }
      const points = gesture.current;
      if (points === null) return;
      if (drag.phase === 'move') {
        const previous = points[points.length - 1];
        if (previous !== undefined && !stepped(transform, previous, drag.point)) return;
        points.push(drag.point);
        setLive([...points]);
        return;
      }
      gesture.current = null;
      setLive(null);
      const finished = [...points, drag.point];
      const geometry = strokeOf(tool, finished, transform.page);
      if (geometry === null) return;
      // A press that went nowhere is a tap, and a tap is not a stroke. `draw` is exempt because
      // its points are already filtered by the same distance on the way in.
      if (geometry.kind !== 'draw' && !stepped(transform, finished[0] ?? drag.point, drag.point)) {
        return;
      }
      commit.current(geometry);
    });
    return () => {
      unsubscribe();
      gesture.current = null;
      setLive(null);
    };
  }, [transform, tool]);

  // A layout effect for the reason `PinLayer` uses one: every shape is rendered
  // `visibility: hidden` and made visible here, so the frame in which a fresh stroke would sit
  // at the sheet's corner never reaches the screen.
  useLayoutEffect(() => {
    if (transform === null) return undefined;
    const project = (): void => {
      // Read per projection rather than once per transform. The sheet's proportions do not
      // change with zoom, but the answer is unavailable for as long as the renderer has
      // nothing open — and a value cached at the one instant it was `null` would leave every
      // measure label blank for the life of the transform, with nothing to recompute it.
      const aspect = sheetAspect(transform);
      const describe = (from: SheetPoint, to: SheetPoint): string =>
        aspect === null ? '' : t('markup.length', { value: percentOfWidth(from, to, aspect) });
      for (const figure of figures) {
        const node = nodes.current.get(figure.key);
        if (node === undefined) continue;
        place(
          node,
          nodes.current.get(labelKey(figure.key)),
          figure.geometry,
          transform,
          describe,
        );
      }
    };
    project();
    return transform.subscribe(project);
  }, [transform, figures, t]);

  if (transform === null) return null;

  /** Registers one node under a key, and forgets it when React takes it away. */
  const keep = (key: string) => (node: SVGElement | null) => {
    if (node === null) nodes.current.delete(key);
    else nodes.current.set(key, node);
  };

  return (
    <svg className={styles.markupLayer}>
      {figures.map((figure) => {
        const { geometry } = figure;
        switch (geometry.kind) {
          case 'draw':
            return (
              <path
                key={figure.key}
                ref={keep(figure.key)}
                className={styles.markupStroke}
                stroke={figure.color}
                style={HIDDEN}
              />
            );
          case 'line':
            return (
              <line
                key={figure.key}
                ref={keep(figure.key)}
                className={styles.markupStroke}
                stroke={figure.color}
                style={HIDDEN}
              />
            );
          case 'box':
            return (
              <rect
                key={figure.key}
                ref={keep(figure.key)}
                className={styles.markupStroke}
                stroke={figure.color}
                style={HIDDEN}
              />
            );
          case 'text':
            return (
              <text
                key={figure.key}
                ref={keep(figure.key)}
                className={styles.markupText}
                fill={figure.color}
                style={HIDDEN}
              >
                {geometry.value}
              </text>
            );
          case 'measure':
            return (
              <g key={figure.key}>
                <line
                  ref={keep(figure.key)}
                  className={styles.markupStroke}
                  stroke={figure.color}
                  style={HIDDEN}
                />
                {/* Empty here on purpose: the number is written by the projection below,
                    which is the only place that knows the sheet's proportions. */}
                <text
                  ref={keep(labelKey(figure.key))}
                  className={styles.markupLabel}
                  fill={figure.color}
                  style={HIDDEN}
                />
              </g>
            );
        }
      })}
    </svg>
  );
}

/**
 * Far enough apart on screen to be two points rather than a wobble.
 *
 * Measured at the current zoom, which is the zoom the gesture is happening at — nothing pans or
 * zooms while a tool holds the drag. A transform with nothing open cannot be mid-gesture, so the
 * `null` branch keeps the point rather than inventing a distance.
 */
function stepped(transform: SheetTransform, from: SheetPoint, to: SheetPoint): boolean {
  const a = transform.toScreen(from);
  const b = transform.toScreen(to);
  if (a === null || b === null) return true;
  return Math.hypot(b.left - a.left, b.top - a.top) >= MIN_STEP_PX;
}

/**
 * Whether the eraser's step from `a` to `b` passed within reach of one figure, in screen space.
 *
 * Tested against the stroke as stored — a freehand stroke's raw polyline, a box's four edges,
 * not its inside, so erasing a stroke drawn inside a box does not take the box with it. Text has
 * no line to pass over, so its rendered box is the target, read off its node.
 */
function touches(
  figure: Figure,
  a: ScreenPoint,
  b: ScreenPoint,
  transform: SheetTransform,
  nodes: ReadonlyMap<string, SVGElement>,
): boolean {
  const { geometry } = figure;
  const near = (from: ScreenPoint, to: ScreenPoint) =>
    segmentDistance(a, b, from, to) <= ERASE_RADIUS_PX;
  const screen = (points: readonly SheetPoint[]): ScreenPoint[] | null => {
    const out: ScreenPoint[] = [];
    for (const point of points) {
      const at = transform.toScreen(point);
      if (at === null) return null;
      out.push(at);
    }
    return out;
  };
  switch (geometry.kind) {
    case 'draw': {
      const points = screen(geometry.points);
      if (points === null) return false;
      if (points.length === 1) return near(points[0] ?? a, points[0] ?? a);
      return points.slice(1).some((to, i) => near(points[i] ?? to, to));
    }
    case 'line':
    case 'measure': {
      const points = screen([geometry.from, geometry.to]);
      return points !== null && near(points[0] ?? a, points[1] ?? a);
    }
    case 'box': {
      const points = screen([geometry.from, geometry.to]);
      if (points === null) return false;
      const [p, q] = points;
      if (p === undefined || q === undefined) return false;
      const corners: ScreenPoint[] = [
        { left: p.left, top: p.top },
        { left: q.left, top: p.top },
        { left: q.left, top: q.top },
        { left: p.left, top: q.top },
      ];
      return corners.some((corner, i) => near(corner, corners[(i + 1) % 4] ?? corner));
    }
    case 'text': {
      const node = nodes.get(figure.key);
      if (!(node instanceof SVGGraphicsElement)) return false;
      const box = node.getBBox();
      const inside = (point: ScreenPoint) =>
        point.left >= box.x - ERASE_RADIUS_PX &&
        point.left <= box.x + box.width + ERASE_RADIUS_PX &&
        point.top >= box.y - ERASE_RADIUS_PX &&
        point.top <= box.y + box.height + ERASE_RADIUS_PX;
      return inside(a) || inside(b);
    }
  }
}

/** The shortest distance between two segments; a segment may be a single point. */
function segmentDistance(a: ScreenPoint, b: ScreenPoint, c: ScreenPoint, d: ScreenPoint): number {
  if (segmentsCross(a, b, c, d)) return 0;
  return Math.min(
    pointToSegment(a, c, d),
    pointToSegment(b, c, d),
    pointToSegment(c, a, b),
    pointToSegment(d, a, b),
  );
}

function pointToSegment(p: ScreenPoint, from: ScreenPoint, to: ScreenPoint): number {
  const dx = to.left - from.left;
  const dy = to.top - from.top;
  const length = dx * dx + dy * dy;
  const t =
    length === 0
      ? 0
      : Math.max(0, Math.min(1, ((p.left - from.left) * dx + (p.top - from.top) * dy) / length));
  return Math.hypot(p.left - (from.left + t * dx), p.top - (from.top + t * dy));
}

function segmentsCross(a: ScreenPoint, b: ScreenPoint, c: ScreenPoint, d: ScreenPoint): boolean {
  const side = (p: ScreenPoint, q: ScreenPoint, r: ScreenPoint) =>
    (q.left - p.left) * (r.top - p.top) - (q.top - p.top) * (r.left - p.left);
  const d1 = side(c, d, a);
  const d2 = side(c, d, b);
  const d3 = side(a, b, c);
  const d4 = side(a, b, d);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/**
 * A freehand stroke as one smooth SVG path, through the midpoints of its segments.
 *
 * Each recorded point becomes the control point of a quadratic curve that runs from the midpoint
 * before it to the midpoint after it, so the line bends *through* the pointer's path instead of
 * turning a corner at every sample. The first and last points are kept exactly — a stroke still
 * starts and ends where the pointer did. Computed in screen space, per projection, so the curve
 * stays smooth at every zoom and the stored points stay the raw samples.
 *
 * No library: `perfect-freehand` solves variable-width, pressure-shaped ink, and this layer draws
 * a constant 2px pen — a midpoint curve is the whole of what that needs.
 */
function smoothPath(points: readonly ScreenPoint[]): string {
  const first = points[0];
  if (first === undefined) return '';
  let d = `M${px(first.left)},${px(first.top)}`;
  if (points.length === 1) return `${d}L${px(first.left)},${px(first.top)}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const control = points[i];
    const next = points[i + 1];
    if (control === undefined || next === undefined) continue;
    const midLeft = (control.left + next.left) / 2;
    const midTop = (control.top + next.top) / 2;
    d += `Q${px(control.left)},${px(control.top)} ${px(midLeft)},${px(midTop)}`;
  }
  const last = points[points.length - 1];
  if (last !== undefined) d += `L${px(last.left)},${px(last.top)}`;
  return d;
}

/**
 * The geometry a gesture has made so far, or `null` when it has not made one yet.
 *
 * The same function serves the stroke being drawn and the stroke being committed, which is
 * what stops the preview and the row disagreeing about what was drawn.
 */
function strokeOf(
  tool: MarkupKind,
  points: readonly SheetPoint[],
  page: number,
): MarkupGeometry | null {
  const from = points[0];
  const to = points[points.length - 1];
  if (from === undefined || to === undefined) return null;
  switch (tool) {
    case 'draw':
      return points.length < 2 ? null : { kind: 'draw', page, points };
    case 'line':
      return { kind: 'line', page, from, to };
    case 'box':
      return { kind: 'box', page, from, to };
    case 'measure':
      return { kind: 'measure', page, from, to };
    case 'text':
      // Not a drag. See the subscription above.
      return null;
  }
}

/**
 * How much wider the sheet is than it is tall, read off the transform rather than guessed.
 *
 * Only the measure label needs it, and it needs it because normalised coordinates are *not* a
 * square frame: `0.1` across an A0 in landscape is a much longer line than `0.1` down it, so a
 * length computed in sheet units alone would be a number about nothing. `null` for the one
 * reason every other answer here is — the renderer has nothing open.
 */
function sheetAspect(transform: SheetTransform): number | null {
  const origin = transform.toScreen({ x: 0, y: 0 });
  const corner = transform.toScreen({ x: 1, y: 1 });
  if (origin === null || corner === null) return null;
  const width = corner.left - origin.left;
  const height = corner.top - origin.top;
  if (width === 0 || height === 0) return null;
  return width / height;
}

/**
 * A measured length, as a percentage of the sheet's width.
 *
 * **Not millimetres, and deliberately not.** Nothing in this product knows the drawing's scale
 * or the paper it was drawn on: the pyramid's DPI is a rendering setting that has already been
 * halved once (ADR-0006), so a millimetre derived from it would be a confident wrong answer.
 * The sheet's own width is the one length both ends of this feature agree on, so the label is
 * honest about being a ratio until somebody decides how a drawing is calibrated.
 *
 * Rounded to one decimal, because the third would be a claim about precision this has none of.
 */
function percentOfWidth(from: SheetPoint, to: SheetPoint, aspect: number): number {
  const across = to.x - from.x;
  const down = (to.y - from.y) / aspect;
  return Math.round(Math.hypot(across, down) * 1000) / 10;
}

/** One decimal of a pixel. Beyond that the attribute is longer than the difference it makes. */
function px(value: number): string {
  return String(Math.round(value * 10) / 10);
}

function reveal(node: SVGElement, label: SVGElement | undefined, shown: boolean): void {
  const visibility = shown ? 'visible' : 'hidden';
  node.style.visibility = visibility;
  if (label !== undefined) label.style.visibility = visibility;
}

/**
 * Write one figure's projected coordinates onto its node.
 *
 * Attributes rather than React props: this runs on every animation frame while the sheet moves
 * (see the file header). `toScreen` answering `null` means the renderer has nothing open, which
 * is the same everywhere on the sheet — so the whole shape hides rather than half of it moving.
 */
function place(
  node: SVGElement,
  label: SVGElement | undefined,
  geometry: MarkupGeometry,
  transform: SheetTransform,
  describe: (from: SheetPoint, to: SheetPoint) => string,
): void {
  switch (geometry.kind) {
    case 'draw': {
      const drawn: ScreenPoint[] = [];
      for (const point of geometry.points) {
        const at = transform.toScreen(point);
        if (at === null) {
          reveal(node, label, false);
          return;
        }
        drawn.push(at);
      }
      node.setAttribute('d', smoothPath(drawn));
      break;
    }
    case 'line':
    case 'measure': {
      const from = transform.toScreen(geometry.from);
      const to = transform.toScreen(geometry.to);
      if (from === null || to === null) {
        reveal(node, label, false);
        return;
      }
      node.setAttribute('x1', px(from.left));
      node.setAttribute('y1', px(from.top));
      node.setAttribute('x2', px(to.left));
      node.setAttribute('y2', px(to.top));
      if (label !== undefined) {
        label.setAttribute('x', px((from.left + to.left) / 2));
        label.setAttribute('y', px((from.top + to.top) / 2));
        label.textContent = describe(geometry.from, geometry.to);
      }
      break;
    }
    case 'box': {
      const from = transform.toScreen(geometry.from);
      const to = transform.toScreen(geometry.to);
      if (from === null || to === null) {
        reveal(node, label, false);
        return;
      }
      // Dragged in any direction: a rectangle has no negative width, so the corners are sorted
      // rather than assumed to have been drawn top-left first.
      node.setAttribute('x', px(Math.min(from.left, to.left)));
      node.setAttribute('y', px(Math.min(from.top, to.top)));
      node.setAttribute('width', px(Math.abs(to.left - from.left)));
      node.setAttribute('height', px(Math.abs(to.top - from.top)));
      break;
    }
    case 'text': {
      const at = transform.toScreen(geometry.at);
      if (at === null) {
        reveal(node, label, false);
        return;
      }
      node.setAttribute('x', px(at.left));
      node.setAttribute('y', px(at.top));
      break;
    }
  }
  reveal(node, label, true);
}
